"use client";

import React from "react";

/**
 * Pagination - Navigation component for paged content.
 */
function PaginationComponent({ children, className = "", size, ...props }) {
  const sizeClasses = {
    sm: "text-sm",
    lg: "text-lg",
  };

  return (
    <nav aria-label="Pagination" {...props}>
      <ul
        className={`flex flex-wrap items-center list-none p-0 m-0 ${sizeClasses[size] || ""} ${className}`.trim()}
      >
        {children}
      </ul>
    </nav>
  );
}

/**
 * Pagination.Item - Individual page number button.
 */
function PaginationItem({
  children,
  active = false,
  disabled = false,
  onClick,
  className = "",
  ...props
}) {
  const baseClasses =
    "relative block px-3 py-1.5 border border-lightGrey3 -ml-px first:ml-0 first:rounded-l last:rounded-r transition-colors";
  const activeClasses = active
    ? "z-10 bg-lightCyan text-white border-lightCyan"
    : "bg-white text-darkBlack hover:bg-lightGrey";
  const disabledClasses = disabled
    ? "pointer-events-none text-darkGrey bg-lightGrey"
    : "cursor-pointer";

  return (
    <li>
      <button
        type="button"
        className={`${baseClasses} ${activeClasses} ${disabledClasses} ${className}`.trim()}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        aria-current={active ? "page" : undefined}
        {...props}
      >
        {children}
      </button>
    </li>
  );
}

/**
 * Pagination.First - Jump to first page button.
 */
function PaginationFirst({ disabled = false, onClick, className = "", ...props }) {
  return (
    <PaginationItem
      disabled={disabled}
      onClick={onClick}
      className={className}
      aria-label="First page"
      {...props}
    >
      <span aria-hidden="true">&laquo;&laquo;</span>
    </PaginationItem>
  );
}

/**
 * Pagination.Prev - Previous page button.
 */
function PaginationPrev({ disabled = false, onClick, className = "", ...props }) {
  return (
    <PaginationItem
      disabled={disabled}
      onClick={onClick}
      className={className}
      aria-label="Previous page"
      {...props}
    >
      <span aria-hidden="true">&lsaquo;</span>
    </PaginationItem>
  );
}

/**
 * Pagination.Next - Next page button.
 */
function PaginationNext({ disabled = false, onClick, className = "", ...props }) {
  return (
    <PaginationItem
      disabled={disabled}
      onClick={onClick}
      className={className}
      aria-label="Next page"
      {...props}
    >
      <span aria-hidden="true">&rsaquo;</span>
    </PaginationItem>
  );
}

/**
 * Pagination.Last - Jump to last page button.
 */
function PaginationLast({ disabled = false, onClick, className = "", ...props }) {
  return (
    <PaginationItem
      disabled={disabled}
      onClick={onClick}
      className={className}
      aria-label="Last page"
      {...props}
    >
      <span aria-hidden="true">&raquo;&raquo;</span>
    </PaginationItem>
  );
}

/**
 * Pagination.Ellipsis - Ellipsis indicator for skipped pages.
 */
function PaginationEllipsis({ className = "", ...props }) {
  return (
    <li>
      <span
        className={`relative block px-3 py-1.5 border border-lightGrey3 -ml-px bg-white text-darkBlack ${className}`.trim()}
        {...props}
      >
        &hellip;
      </span>
    </li>
  );
}

export const Pagination = Object.assign(PaginationComponent, {
  Item: PaginationItem,
  First: PaginationFirst,
  Prev: PaginationPrev,
  Next: PaginationNext,
  Last: PaginationLast,
  Ellipsis: PaginationEllipsis,
});

